import Delete16 from '@carbon/icons-react/es/delete/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Information16 from '@carbon/icons-react/es/information/16';
import TextNewLine16 from '@carbon/icons-react/es/text--new-line/16';
import { Tooltip } from 'antd';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Popover } from 'components/atoms/popover';
import { Tag } from 'components/atoms/tag';
import { Action } from 'components/molecules/action-footer';
import { ConfirmationPopover } from 'components/molecules/action-panel/confirmation-popover';
import { Filter } from 'components/molecules/filters/types';
import { FilterCriteriaAsText } from 'components/organisms/dashboard/filter-as-text';
import {
  ProjectTemplateData,
  ProjectTemplateSpaceData,
} from 'data/templates/projects';
import { selectParentName } from 'data/templates/projects/selectors';
import { getRandomColor } from 'utils/get-random-color';

const StyledTextNewLine16 = styled(TextNewLine16)`
  overflow: visible !important;
  margin-right: var(--spacing-04);
  color: var(--icon-secondary);
`;

const TagWrapper = styled.div`
  margin-right: var(--spacing-03);
  .ant-tag {
    margin: 0;
  }
`;

const StyledEditIcon = styled(Edit16)`
  overflow: visible !important;
  height: 100%;
  cursor: pointer;
  &:hover {
    color: var(--primary-color);
  }
`;

const StyledDeleteIcon = styled(Delete16)`
  overflow: visible !important;
  height: 100%;
  margin-left: var(--spacing-05);
  cursor: pointer;
  &:hover {
    color: var(--error-color);
  }
`;

const StyledInformation16 = styled(Information16)`
  position: relative;
  right: var(--spacing-03);
  cursor: pointer;
  color: var(--icon-secondary);
  &.visible {
    visibility: visible;
  }
  &.hidden {
    visibility: hidden;
  }
`;

const StyledEditIconWrapper = styled.div`
  position: sticky;
  right: 0;
  background-color: var(--item-hover-bg);
  padding-left: var(--spacing-04);
  padding-right: var(--spacing-05);
  align-self: stretch;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const FlexOne = styled(Flex)`
  flex: 1;
`;

interface WrapperProps {
  $hierarchyLevel: number;
}

const Wrapper = styled(Flex)<WrapperProps>`
  white-space: nowrap;
  padding-left: ${(props) =>
    `calc(${props.$hierarchyLevel} * var(--spacing-06))`};
`;

const StyledConfirmationPopover = styled(ConfirmationPopover)`
  .ant-btn-link {
    color: var(--error-color);
  }
`;

export const DynamicConditionsRow: FC<{
  id: number;
  record: ProjectTemplateSpaceData | ProjectTemplateData;
  hierarchyLevel: number;
  filter: Filter;
  onEditClick: VoidFunction;
  onDeleteConfirmClick: VoidFunction;
  selectedRecord?: ProjectTemplateSpaceData;
}> = ({
  id,
  record,
  hierarchyLevel,
  filter: _filter,
  onEditClick,
  onDeleteConfirmClick,
  selectedRecord,
}) => {
  const divRef = useRef({} as HTMLDivElement);
  const [hovered, setHovered] = useState(false);
  const parentName = useSelector(selectParentName(id, selectedRecord!));

  const i18nLabel = useMemo(() => {
    if (parentName) {
      return 'templates.dynamic.conditions.column.created.with.parent.text';
    }

    if (record?.type === 'PROJECT_PHASE') {
      return 'templates.dynamic.conditions.column.created.text';
    }

    if (record?.type === 'PROJECT_SPACE') {
      return 'templates.dynamic.conditions.column.created.text.space';
    }

    if (record?.type === 'SPACE_TAB') {
      return 'templates.dynamic.conditions.column.created.text.document';
    }

    return '';
  }, [parentName, record?.type]);

  const _onEditClick = useCallback(() => {
    setHovered(false);
    onEditClick();
  }, [onEditClick]);

  const _onDeleteClick = useCallback(() => {}, []);

  // Ref: https://github.com/facebook/react/issues/4492#issuecomment-569410248
  useEffect(() => {
    if (
      hovered &&
      divRef.current &&
      divRef.current?.matches(':hover') === false
    ) {
      setHovered(false);
    }
  }, [hovered]);

  const deleteAction: Action = useMemo(
    () => ({
      title: <FormattedMessage id="delete" />,
      onClick: onDeleteConfirmClick,
    }),
    [onDeleteConfirmClick],
  );

  const cancelAction: Action = useMemo(
    () => ({
      title: <FormattedMessage id="cancel" />,
      onClick: () => setHovered(false),
    }),
    [],
  );

  const onMouseEnter = useCallback(() => setHovered(true), []);
  const onMouseLeave = useCallback(() => setHovered(false), []);

  return (
    <Wrapper
      $hierarchyLevel={hierarchyLevel}
      ref={divRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Tooltip
        title={
          <FormattedMessage
            id={i18nLabel}
            values={{ parentName, b: (chunks) => <strong>{chunks}</strong> }}
          />
        }
      >
        <StyledInformation16 className={hovered ? 'visible' : 'hidden'} />
      </Tooltip>
      {hierarchyLevel > 0 && <StyledTextNewLine16 />}
      <FlexOne>
        {_filter.criteria.map((fieldFilter) => {
          const color = getRandomColor(Math.abs(fieldFilter.field.fieldId));

          return (
            <TagWrapper key={fieldFilter.id}>
              <Tag color={color}>
                <FilterCriteriaAsText criterion={fieldFilter} />
              </Tag>
            </TagWrapper>
          );
        })}
      </FlexOne>
      {hovered && (
        <StyledEditIconWrapper>
          <Tooltip
            placement="left"
            title={
              <FormattedMessage id="templates.dynamic.conditions.edit.tooltip" />
            }
          >
            <StyledEditIcon onClick={_onEditClick} />
          </Tooltip>
          <Tooltip
            placement="left"
            title={
              <FormattedMessage id="templates.dynamic.conditions.delete.tooltip" />
            }
          >
            <Popover
              trigger="click"
              placement="bottomRight"
              content={
                <StyledConfirmationPopover
                  title={<FormattedMessage id="delete" />}
                  confirmationText={
                    <FormattedMessage id="templates.dynamic.conditions.delete.confirmation.text" />
                  }
                  ok={deleteAction}
                  cancel={cancelAction}
                  reverseCta
                />
              }
            >
              <StyledDeleteIcon onClick={_onDeleteClick} />
            </Popover>
          </Tooltip>
        </StyledEditIconWrapper>
      )}
    </Wrapper>
  );
};
